import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, ChevronRight } from 'lucide-react';

const sections = [
  { id: 'introduction', num: '01', title: 'Introduction', content: (
    <p>
      Expertize Edge ("we", "our", or "us") is committed to protecting your privacy. 
      This Privacy Policy explains how we collect, use, disclose, and safeguard your 
      information when you visit our website or use our services.
    </p>
  )},
  { id: 'information-collect', num: '02', title: 'Information We Collect', content: (
    <>
      <p className="mb-4">We may collect information about you in various ways, including:</p>
      <ul className="space-y-3">
        <li className="flex items-start gap-3">
          <ChevronRight className="w-5 h-5 text-brand-purple flex-shrink-0 mt-0.5" />
          <span><strong className="text-brand-charcoal">Personal Data:</strong> Name, email address, phone number, company name, and other information you voluntarily provide when contacting us or using our services.</span>
        </li>
        <li className="flex items-start gap-3">
          <ChevronRight className="w-5 h-5 text-brand-purple flex-shrink-0 mt-0.5" />
          <span><strong className="text-brand-charcoal">Usage Data:</strong> Information about how you interact with our website, including pages visited, time spent, and navigation patterns.</span>
        </li>
        <li className="flex items-start gap-3">
          <ChevronRight className="w-5 h-5 text-brand-purple flex-shrink-0 mt-0.5" />
          <span><strong className="text-brand-charcoal">Technical Data:</strong> IP address, browser type, device information, and other technical identifiers.</span>
        </li>
      </ul>
    </>
  )},
  { id: 'how-we-use', num: '03', title: 'How We Use Your Information', content: (
    <>
      <p className="mb-4">We use the information we collect to:</p>
      <ul className="space-y-3">
        {['Provide, operate, and maintain our services', 'Respond to your inquiries and fulfill your requests', 'Send you administrative information and service updates', 'Improve our website and services', 'Comply with legal obligations'].map((item, i) => (
          <li key={i} className="flex items-start gap-3">
            <ChevronRight className="w-5 h-5 text-brand-purple flex-shrink-0 mt-0.5" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </>
  )},
  { id: 'information-sharing', num: '04', title: 'Information Sharing', content: (
    <p>
      We do not sell, trade, or otherwise transfer your personal information to third parties 
      without your consent, except as necessary to provide our services or as required by law. 
      We may share information with trusted service providers who assist us in operating our 
      website and conducting our business, provided they agree to keep this information confidential.
    </p>
  )},
  { id: 'data-security', num: '05', title: 'Data Security', content: (
    <p>
      We implement appropriate technical and organizational security measures to protect your 
      personal information against unauthorized access, alteration, disclosure, or destruction. 
      However, no method of transmission over the Internet or electronic storage is 100% secure.
    </p>
  )},
  { id: 'data-retention', num: '06', title: 'Data Retention', content: (
    <p>
      We retain your personal information only for as long as necessary to fulfill the purposes 
      for which it was collected, or as required by applicable laws and regulations.
    </p>
  )},
  { id: 'your-rights', num: '07', title: 'Your Rights', content: (
    <>
      <p className="mb-4">Depending on your location, you may have the right to:</p>
      <ul className="space-y-3">
        {['Access the personal information we hold about you', 'Request correction of inaccurate information', 'Request deletion of your personal information', 'Object to or restrict processing of your information', 'Withdraw consent where processing is based on consent'].map((item, i) => (
          <li key={i} className="flex items-start gap-3">
            <ChevronRight className="w-5 h-5 text-brand-purple flex-shrink-0 mt-0.5" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </>
  )},
  { id: 'cookies', num: '08', title: 'Cookies', content: (
    <p>
      Our website may use cookies and similar tracking technologies to enhance your browsing 
      experience. You can set your browser to refuse all or some browser cookies, but this 
      may affect your ability to access certain features of our website.
    </p>
  )},
  { id: 'third-party', num: '09', title: 'Third-Party Links', content: (
    <p>
      Our website may contain links to third-party websites. We are not responsible for the 
      privacy practices or content of these external sites. We encourage you to read the 
      privacy policies of any third-party sites you visit.
    </p>
  )},
  { id: 'changes', num: '10', title: 'Changes to This Policy', content: (
    <p>
      We may update this Privacy Policy from time to time. We will notify you of any changes 
      by posting the new Privacy Policy on this page and updating the "Last updated" date.
    </p>
  )},
  { id: 'contact', num: '11', title: 'Contact Us', content: (
    <>
      <p className="mb-4">If you have any questions about this Privacy Policy, please contact us at:</p>
      <div className="bg-brand-charcoal rounded-xl p-6 text-white">
        <p className="font-semibold text-brand-gold mb-2">Expertize Edge</p>
        <p className="text-gray-300">Email: contact@expertizeedge.com</p>
        <p className="text-gray-300">Phone: +91 95757 37655</p>
        <p className="text-gray-300">Gandhinagar, Gujarat, India</p>
      </div>
    </>
  )},
];

const PrivacyPolicyPage = () => {
  const [activeSection, setActiveSection] = useState('introduction');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-100px 0px -60% 0px' }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <main className="pt-20">
      {/* Hero */}
      <section className="relative py-24 bg-brand-charcoal overflow-hidden" data-testid="privacy-hero">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(92,45,145,0.25),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_70%,rgba(196,158,77,0.15),transparent_50%)]" />
        </div>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/3 right-1/4 w-40 h-40 bg-brand-purple/10 rounded-full blur-3xl"
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 bg-brand-gold/20 border border-brand-gold/30 rounded-full px-4 py-2 mb-6">
              <Shield className="w-4 h-4 text-brand-gold" />
              <span className="text-brand-gold text-sm font-medium">Your Privacy Matters</span>
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl font-bold text-white mb-4">
              Privacy <span className="text-brand-gold">Policy</span>
            </h1>
            <p className="text-gray-400 text-lg">Last updated: January 2026</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-brand-bg-light" data-testid="privacy-content">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-12">
            {/* Sidebar TOC */}
            <aside className="hidden lg:block">
              <nav className="sticky top-28 space-y-1">
                {sections.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className={`block text-sm px-3 py-2 rounded-lg transition-all duration-200 ${
                      activeSection === s.id
                        ? 'bg-brand-purple/10 text-brand-purple font-semibold'
                        : 'text-brand-muted hover:text-brand-charcoal hover:bg-white'
                    }`}
                  >
                    <span className="text-xs text-brand-gold mr-2">{s.num}</span>
                    {s.title}
                  </a>
                ))}
              </nav>
            </aside>

            {/* Main Content */}
            <div className="space-y-8">
              {sections.map((s, index) => (
                <motion.div
                  key={s.id}
                  id={s.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.03 }}
                  className="bg-white rounded-2xl p-8 md:p-10 border border-brand-lavender hover:border-brand-purple/20 hover:shadow-lg transition-all duration-300"
                  data-testid={`privacy-section-${s.id}`}
                >
                  <div className="flex items-center gap-3 mb-5">
                    <span className="text-xs font-bold text-brand-gold bg-brand-gold/10 px-2.5 py-1 rounded-md">{s.num}</span>
                    <h2 className="font-heading text-2xl font-bold text-brand-charcoal">{s.title}</h2>
                  </div>
                  <div className="text-brand-body leading-relaxed">
                    {s.content}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PrivacyPolicyPage;
